import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { Heading, Text, Button } from '@shinkansen/sakura'
import { BrandLockup } from '~/components/BrandLockup'
import { useAuth } from '~/auth'
import { requestPhoneOtp, verifyPhoneOtp } from '~/auth/auth-client'
import styles from './auth.module.css'

const MIN_PASSWORD_LENGTH = 8

type RegisterStep = 'details' | 'verifyPhone' | 'verifyEmail'

// Map auth-server error codes/messages to human-friendly copy.
function mapRegisterError(error: string): string {
  if (error.includes('already exists')) {
    return 'An account with this email already exists'
  }
  switch (error) {
    case 'password_too_weak':
      return 'Please choose a stronger password'
    case 'registration_unavailable':
      return 'Registration is temporarily unavailable. Please try again later.'
    case 'invalid_phone_verification':
      return 'Verify your phone number before creating the account'
    case 'otp_unavailable':
      return 'Phone verification is temporarily unavailable'
    default:
      return 'Unable to create your account. Please try again.'
  }
}

function hasEnoughDigits(value: string) {
  return value.replace(/\D/g, '').length >= 8
}

export function RegisterPage() {
  const navigate = useNavigate()
  const { signUp } = useAuth()

  const [step, setStep] = useState<RegisterStep>('details')
  const [phone, setPhone] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [otpCode, setOtpCode] = useState('')
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const validateDetails = () => {
    if (!hasEnoughDigits(phone)) {
      setError('Enter a valid phone number')
      return false
    }
    if (!email.trim()) {
      setError('Email is required for the WorkOS account')
      return false
    }
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters`)
      return false
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return false
    }
    return true
  }

  const handleDetailsSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (isSubmitting) return

    setError('')
    setNotice('')
    if (!validateDetails()) return

    setIsSubmitting(true)
    try {
      const result = await requestPhoneOtp(phone)
      if (!result.ok) {
        setError(mapRegisterError(result.error))
        return
      }
      setNotice(`Code sent to ${result.to ?? 'your phone'}.`)
      setStep('verifyPhone')
    } finally {
      setIsSubmitting(false)
    }
  }

  const createEmailBackedAccount = async (phoneVerificationToken: string) => {
    setIsSubmitting(true)
    try {
      const result = await signUp({
        email: email.trim(),
        password,
        firstName: firstName || undefined,
        lastName: lastName || undefined,
        phone,
        phoneVerificationToken,
      })
      if (result.ok) {
        await navigate({ to: '/' })
        return
      }
      if (result.error === 'email_verification_required') {
        setNotice('Phone verified. Check your email to finish WorkOS verification.')
        setStep('verifyEmail')
        return
      }
      setError(mapRegisterError(result.error))
    } catch {
      setError('Unable to create your account. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleOtpSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (isSubmitting) return

    setError('')
    setNotice('')
    if (otpCode.replace(/\D/g, '').length !== 6) {
      setError('Wrong code - enter the 6-digit code we sent')
      return
    }

    setIsSubmitting(true)
    const verification = await verifyPhoneOtp(phone, otpCode)
    if (!verification.ok) {
      setIsSubmitting(false)
      setError(verification.error === 'otp_unavailable' ? mapRegisterError(verification.error) : 'Wrong code - enter the 6-digit code we sent')
      return
    }
    if (!verification.phoneVerificationToken) {
      setIsSubmitting(false)
      setError('Wrong code - enter the 6-digit code we sent')
      return
    }
    setIsSubmitting(false)

    await createEmailBackedAccount(verification.phoneVerificationToken)
  }

  return (
    <div className={styles.page}>
      <span className={styles.bgNumber}>02</span>

      <div className={styles.container}>
        <header className={styles.header}>
          <BrandLockup variant="auth" meta="MODERN EGG DISTRIBUTOR" />
        </header>

        <main className={styles.main}>
          <Heading level="h2" size="xl" className={styles.formTitle}>
            REGISTER
          </Heading>

          {error && <div className={styles.error}>{error}</div>}
          {notice && <div className={styles.success}>{notice}</div>}

          {step === 'details' && (
            <form className={styles.form} onSubmit={handleDetailsSubmit}>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="phone">
                  PHONE NUMBER
                </label>
                <div className={styles.identifierRow}>
                  <span className={styles.countryPrefix}>ID +62</span>
                  <input
                    className={`${styles.input} ${styles.inlineInput}`}
                    id="phone"
                    name="phone"
                    type="tel"
                    inputMode="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    autoComplete="tel"
                    autoFocus
                    required
                  />
                </div>
                <span className={styles.helperText}>
                  Code arrives by SMS. We verify this phone before account creation.
                </span>
              </div>

              <div className={styles.fieldRow}>
                <div className={styles.field}>
                  <label className={styles.label} htmlFor="firstName">
                    FIRST NAME
                  </label>
                  <input
                    className={styles.input}
                    id="firstName"
                    name="firstName"
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    autoComplete="given-name"
                  />
                </div>

                <div className={styles.field}>
                  <label className={styles.label} htmlFor="lastName">
                    LAST NAME
                  </label>
                  <input
                    className={styles.input}
                    id="lastName"
                    name="lastName"
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    autoComplete="family-name"
                  />
                </div>
              </div>

              <div className={styles.field}>
                <label className={styles.label} htmlFor="email">
                  EMAIL <span className={styles.helperText}>WORKOS ACCOUNT</span>
                </label>
                <input
                  className={styles.input}
                  id="email"
                  name="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  placeholder="Required for WorkOS sign-in"
                  required
                />
              </div>

              <div className={styles.field}>
                <label className={styles.label} htmlFor="password">
                  PASSWORD
                </label>
                <input
                  className={styles.input}
                  id="password"
                  name="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  minLength={MIN_PASSWORD_LENGTH}
                  required
                />
              </div>

              <div className={styles.field}>
                <label className={styles.label} htmlFor="confirmPassword">
                  CONFIRM PASSWORD
                </label>
                <input
                  className={styles.input}
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                />
              </div>

              <Button type="submit" variant="accent" size="lg" width="full" disabled={isSubmitting}>
                {isSubmitting ? 'SENDING CODE...' : 'SEND CODE'}
              </Button>
            </form>
          )}

          {step === 'verifyPhone' && (
            <form className={styles.form} onSubmit={handleOtpSubmit}>
              <div className={styles.noticeCard}>
                <span className={styles.noticeTitle}>VERIFY YOUR PHONE</span>
                <Text variant="muted" size="sm">
                  Enter the 6-digit Twilio code sent to ID +62 {phone}.
                </Text>
              </div>

              <div className={styles.field}>
                <label className={styles.label} htmlFor="otpCode">
                  VERIFICATION CODE
                </label>
                <input
                  className={styles.otpInput}
                  id="otpCode"
                  name="otpCode"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                  autoComplete="one-time-code"
                  autoFocus
                  required
                />
                <span className={styles.helperText}>
                  Need another code? Go back and send a new one.
                </span>
              </div>

              <Button type="submit" variant="accent" size="lg" width="full" disabled={isSubmitting}>
                {isSubmitting ? 'CREATING ACCOUNT...' : 'VERIFY & CREATE ACCOUNT'}
              </Button>
            </form>
          )}

          {step === 'verifyEmail' && (
            <div className={styles.noticeCard}>
              <div className={styles.success}>
                Phone verified - your account was created.
              </div>
              <span className={styles.noticeTitle}>VERIFY YOUR EMAIL</span>
              <Text variant="muted" size="sm">
                WorkOS requires email verification before sign-in. Check {email}, then return to login.
              </Text>
              <div className={styles.noticeActions}>
                <Link className={styles.secondaryButton} to="/login" search={{ reset: undefined }}>
                  BACK TO LOGIN
                </Link>
              </div>
            </div>
          )}

          <div className={styles.footer}>
            <Text variant="muted" size="sm">
              ALREADY HAVE AN ACCOUNT?
            </Text>
            <Link className={styles.footerLink} to="/login" search={{ reset: undefined }}>
              SIGN IN
            </Link>
          </div>
        </main>
      </div>
    </div>
  )
}
